import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection, getDocs, query, orderBy, doc, onSnapshot,
  addDoc, setDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import BlogNavbar from './BlogNavbar';
import BlogFooter from './BlogFooter';
import { FaPlay, FaPause, FaMicrophone, FaUndo, FaRedo, FaRss, FaShareAlt, FaBroadcastTower, FaStop } from 'react-icons/fa';
import './Podcast.css';
import SEO, { podcastSeriesSchema } from '../components/SEO';

const fmtTime = (s) => {
  if (!s || !isFinite(s) || isNaN(s)) return '--:--';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0
    ? `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`
    : `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
};

const fmtDuration = (s) => {
  if (!s || !isFinite(s) || isNaN(s)) return '';
  const mins = Math.round(s / 60);
  return mins < 1 ? '< 1 min' : `${mins} min`;
};

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

const toDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const CoverPlaceholder = ({ epNum }) => (
  <div className="poc-cover-placeholder">
    <FaMicrophone className="poc-cover-mic" />
    <span>Ep {epNum}</span>
  </div>
);

const EpSkeleton = () => (
  <div className="poc-ep-card poc-ep-skel" aria-hidden="true">
    <div className="poc-ep-card-img poc-skel-block" />
    <div className="poc-ep-card-body">
      <div className="poc-skel-line" style={{ width: '40%', height: 12 }} />
      <div className="poc-skel-line" style={{ width: '80%', height: 20, margin: '8px 0' }} />
      <div className="poc-skel-line" style={{ width: '60%', height: 14 }} />
      <div className="poc-skel-line" style={{ width: '95%', height: 14, marginTop: 8 }} />
    </div>
  </div>
);

export default function PodcastPage() {
  const [episodes, setEpisodes]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [playerEp, setPlayerEp]       = useState(null);
  const [playerPlaying, setPlaying]   = useState(false);
  const [playerTime, setPlayerTime]   = useState(0);
  const [playerDur, setPlayerDur]     = useState(0);
  const [playerSpeed, setPlayerSpeed] = useState(1);
  const [copied, setCopied]           = useState(null);
  const [liveSession, setLiveSession] = useState(null);
  const [liveStatus, setLiveStatus]   = useState('idle');
  const [liveError, setLiveError]     = useState('');
  const [liveComments, setLiveComments] = useState([]);
  const [liveName, setLiveName] = useState('');
  const [liveCommentText, setLiveCommentText] = useState('');
  const [liveListenerCount, setLiveListenerCount] = useState(0);
  const [liveClock, setLiveClock] = useState({ label: 'Off air', tone: 'off' });
  const liveScheduled = !!liveSession?.scheduledAt && !liveSession?.isLive;

  const audioRef    = useRef(new Audio());
  const progressRef = useRef(null);
  const liveRoomRef = useRef(null);
  const liveAudioMountRef = useRef(null);
  const liveListenerIdRef = useRef(`listener-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const liveHeartbeatRef = useRef(null);

  /* ── load published episodes ── */
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'podcast_episodes'), orderBy('episodeNum', 'asc'))
        );
        setEpisodes(
          snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(e => e.published)
        );
      } catch (_) {}
      finally { setLoading(false); }
    })();
  }, []);

  /* ── wire audio events ── */
  useEffect(() => {
    const a = audioRef.current;
    const onTime  = () => setPlayerTime(a.currentTime);
    const onMeta  = () => setPlayerDur(isNaN(a.duration) || !isFinite(a.duration) ? 0 : a.duration);
    const onEnded = () => { setPlaying(false); setPlayerTime(0); };
    a.addEventListener('timeupdate',      onTime);
    a.addEventListener('loadedmetadata',  onMeta);
    a.addEventListener('durationchange',  onMeta);
    a.addEventListener('ended',           onEnded);
    a.addEventListener('play',   () => setPlaying(true));
    a.addEventListener('pause',  () => setPlaying(false));
    return () => {
      a.removeEventListener('timeupdate',     onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('durationchange', onMeta);
      a.removeEventListener('ended',          onEnded);
    };
  }, []);

  useEffect(() => () => { audioRef.current.pause(); audioRef.current.src = ''; }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'podcast_live', 'current'), snap => {
      const data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      setLiveSession(data);
      if (!data?.isLive && liveRoomRef.current) {
        liveRoomRef.current.disconnect();
        liveRoomRef.current = null;
        clearInterval(liveHeartbeatRef.current);
        liveHeartbeatRef.current = null;
        deleteDoc(doc(db, 'podcast_live', 'current', 'listeners', liveListenerIdRef.current)).catch(() => {});
        setLiveStatus('idle');
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsubComments = onSnapshot(query(collection(db, 'podcast_live', 'current', 'comments'), orderBy('createdAt', 'asc')), snap => {
      setLiveComments(snap.docs.map(d => ({ id: d.id, ...d.data() })).slice(-40));
    });
    const unsubListeners = onSnapshot(collection(db, 'podcast_live', 'current', 'listeners'), snap => {
      const now = Date.now();
      const active = snap.docs.filter(d => {
        const data = d.data();
        const lastSeen = toDate(data.lastSeen)?.getTime() || 0;
        return data.status === 'connected' && now - lastSeen < 45000;
      }).length;
      setLiveListenerCount(active);
    });
    return () => {
      unsubComments();
      unsubListeners();
    };
  }, []);

  useEffect(() => () => {
    liveRoomRef.current?.disconnect();
    clearInterval(liveHeartbeatRef.current);
    deleteDoc(doc(db, 'podcast_live', 'current', 'listeners', liveListenerIdRef.current)).catch(() => {});
  }, []);

  useEffect(() => {
    const tick = () => {
      if (liveSession?.isLive) {
        const started = toDate(liveSession.startedAt)?.getTime();
        const secs = started ? Math.max(0, Math.floor((Date.now() - started) / 1000)) : 0;
        setLiveClock({ label: `Live for ${fmtTime(secs)}`, tone: 'live' });
        return;
      }
      const scheduled = toDate(liveSession?.scheduledAt)?.getTime();
      if (scheduled && scheduled > Date.now()) {
        const secs = Math.floor((scheduled - Date.now()) / 1000);
        const days = Math.floor(secs / 86400);
        const hours = Math.floor((secs % 86400) / 3600);
        const mins = Math.floor((secs % 3600) / 60);
        const label = days > 0 ? `Starts in ${days}d ${hours}h` : hours > 0 ? `Starts in ${hours}h ${mins}m` : `Starts in ${Math.max(1, mins)}m`;
        setLiveClock({ label, tone: 'scheduled' });
        return;
      }
      setLiveClock({ label: scheduled ? 'Scheduled soon' : 'Off air', tone: 'off' });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [liveSession]);

  /* ── player actions ── */
  const loadEp = useCallback((ep) => {
    if (!ep.audioUrl) return;
    if (playerEp?.id === ep.id) {
      playerPlaying ? audioRef.current.pause() : audioRef.current.play().catch(() => {});
      return;
    }
    const a = audioRef.current;
    a.pause();
    a.src = ep.audioUrl;
    a.playbackRate = playerSpeed;
    a.load();
    a.play().catch(() => {});
    setPlayerEp(ep);
    setPlayerTime(0);
    setPlayerDur(ep.duration || 0);
  }, [playerEp, playerPlaying, playerSpeed]);

  const togglePlay = () => {
    if (!playerEp) return;
    playerPlaying ? audioRef.current.pause() : audioRef.current.play().catch(() => {});
  };

  const skip = (s) => {
    const a = audioRef.current;
    if (!a.src) return;
    a.currentTime = Math.max(0, Math.min(a.duration || 0, a.currentTime + s));
  };

  const seek = (e) => {
    const bar = progressRef.current;
    if (!bar || !playerDur) return;
    const r = bar.getBoundingClientRect();
    audioRef.current.currentTime = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * playerDur;
  };

  const changeSpeed = (s) => {
    setPlayerSpeed(s);
    audioRef.current.playbackRate = s;
  };

  const closePlayer = () => {
    audioRef.current.pause();
    audioRef.current.src = '';
    setPlayerEp(null);
    setPlaying(false);
    setPlayerTime(0);
    setPlayerDur(0);
  };

  const shareEp = (ep) => {
    const url = `${window.location.origin}/podcast#ep-${ep.episodeNum}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(ep.id);
      setTimeout(() => setCopied(null), 2200);
    }).catch(() => {});
  };

  const joinLive = async () => {
    if (liveStatus === 'connecting' || liveStatus === 'connected') return;
    setLiveError('');
    setLiveStatus('connecting');
    try {
      const { Room, RoomEvent } = await import('livekit-client');
      const res = await fetch('/api/livekit/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: 'audience' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not join live podcast.');

      const room = new Room({ adaptiveStream: true });
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind !== 'audio') return;
        const el = track.attach();
        el.autoplay = true;
        el.playsInline = true;
        if (liveAudioMountRef.current) {
          liveAudioMountRef.current.innerHTML = '';
          liveAudioMountRef.current.appendChild(el);
        }
        el.play?.().catch(() => {});
      });
      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        track.detach().forEach(el => el.remove());
      });
      room.on(RoomEvent.Disconnected, () => {
        setLiveStatus('idle');
        liveRoomRef.current = null;
        if (liveAudioMountRef.current) liveAudioMountRef.current.innerHTML = '';
      });
      await room.connect(data.url, data.token);
      liveRoomRef.current = room;
      setLiveStatus('connected');
      const listenerRef = doc(db, 'podcast_live', 'current', 'listeners', liveListenerIdRef.current);
      await setDoc(listenerRef, {
        displayName: liveName.trim() || 'Listener',
        status: 'connected',
        joinedAt: serverTimestamp(),
        lastSeen: serverTimestamp(),
      });
      clearInterval(liveHeartbeatRef.current);
      liveHeartbeatRef.current = setInterval(() => {
        setDoc(listenerRef, {
          displayName: liveName.trim() || 'Listener',
          status: 'connected',
          lastSeen: serverTimestamp(),
        }, { merge: true }).catch(() => {});
      }, 20000);
    } catch (err) {
      setLiveError(err.message || 'Could not join live podcast.');
      setLiveStatus('idle');
    }
  };

  const leaveLive = () => {
    liveRoomRef.current?.disconnect();
    liveRoomRef.current = null;
    clearInterval(liveHeartbeatRef.current);
    liveHeartbeatRef.current = null;
    deleteDoc(doc(db, 'podcast_live', 'current', 'listeners', liveListenerIdRef.current)).catch(() => {});
    setLiveStatus('idle');
    if (liveAudioMountRef.current) liveAudioMountRef.current.innerHTML = '';
  };

  const sendLiveComment = async (e) => {
    e.preventDefault();
    const text = liveCommentText.trim();
    if (!text) return;
    try {
      await addDoc(collection(db, 'podcast_live', 'current', 'comments'), {
        name: liveName.trim() || 'Listener',
        text: text.slice(0, 500),
        createdAt: serverTimestamp(),
      });
      setLiveCommentText('');
    } catch (_) {
      setLiveError('Could not send comment. Please try again.');
    }
  };


  const pct = playerDur > 0 ? (playerTime / playerDur) * 100 : 0;
  const latestEpisode = episodes.length ? episodes[episodes.length - 1] : null;
  const archiveEpisodes = episodes.length > 1 ? [...episodes.slice(0, -1)].reverse() : [];

  return (
    <div className="poc-page">
      <SEO
        title="The PP Podcast — Contracts, Freelance & Business Insights"
        description="Simple, practical podcast episodes from Pooja Biswal on contracts, freelancing, business basics, and protecting your work."
        keywords="contract law podcast, freelance business podcast, commercial contracts podcast, legal advice podcast UK, contract specialist podcast, Pooja Biswal podcast"
        canonical="/podcast"
        schema={podcastSeriesSchema(episodes)}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "The PP Podcast", path: "/podcast" }]}
      />
      <BlogNavbar />

      {/* ── HERO ── */}
      <section className="poc-hero">
        <div className="poc-hero-inner">
          <div className="poc-show-art" aria-hidden="true">
            <div className="poc-show-art-top">A podcast by Pooja Biswal</div>
            <FaMicrophone />
            <strong>The PP<br />Podcast</strong>
            <span>Contracts · Work · Life</span>
            <div className="poc-show-wave"><i/><i/><i/><i/><i/><i/><i/><i/><i/></div>
          </div>
          <div className="poc-hero-copy">
            <p className="poc-hero-kicker">The PP Podcast</p>
            <h1 className="poc-hero-title">Clear conversations for the work you’re building.</h1>
            <p className="poc-hero-sub">
              Pooja makes contracts, clients, money and modern working life easier to understand—one thoughtful conversation at a time.
            </p>
            <div className="poc-hero-actions">
              {latestEpisode?.audioUrl && (
                <button className="poc-hero-play" onClick={() => loadEp(latestEpisode)}>
                  <FaPlay /> Listen to the latest
                </button>
              )}
              <a href="/api/podcast/rss" target="_blank" rel="noreferrer" className="poc-rss-btn">
                <FaRss /> Follow the show
              </a>
            </div>
            <div className="poc-show-proof">
              <span><b>{loading ? '—' : episodes.length}</b> episodes</span>
              <span><b>Practical</b> takeaways</span>
              <span><b>Hosted by</b> Pooja</span>
            </div>
          </div>
        </div>
      </section>

      {(liveSession?.isLive || liveScheduled) && (
      <section className={`poc-live-strip ${liveSession?.isLive ? 'is-live' : 'is-scheduled'}`}>
        <div className="poc-live-inner">
          <div className="poc-live-copy">
            <span className="poc-live-icon"><FaBroadcastTower /></span>
            <div>
              <strong>{liveSession?.isLive ? 'Pooja is live now' : liveScheduled ? 'Next live session' : 'Live podcast'}</strong>
              <p>{liveSession?.isLive || liveScheduled ? (liveSession.title || 'Join the live audio room') : 'When Pooja starts a live session, it will appear here.'}</p>
              <div className="poc-live-meta">
                <span className={`poc-live-clock ${liveClock.tone}`}>{liveClock.label}</span>
                <span>{liveListenerCount} listening</span>
                <span>{liveComments.length} comments</span>
              </div>
            </div>
          </div>
          <div className="poc-live-actions">
            <div ref={liveAudioMountRef} className="poc-live-audio" aria-hidden="true" />
            {liveSession?.isLive ? (
              liveStatus === 'connected' ? (
                <button className="poc-live-btn poc-live-btn-stop" onClick={leaveLive}><FaStop /> Leave live</button>
              ) : (
                <button className="poc-live-btn" onClick={joinLive} disabled={liveStatus === 'connecting'}>
                  <FaPlay /> {liveStatus === 'connecting' ? 'Connecting...' : 'Listen live'}
                </button>
              )
            ) : (
              <span className="poc-live-waiting">Off air</span>
            )}
          </div>
        </div>
        {liveError && <div className="poc-live-error">{liveError}</div>}
        {(liveSession?.isLive || liveComments.length > 0) && (
          <div className="poc-live-chat">
            <div className="poc-live-chat-list">
              {liveComments.length === 0 ? (
                <p className="poc-live-chat-empty">Live comments will appear here.</p>
              ) : liveComments.map(c => (
                <div key={c.id} className="poc-live-chat-msg">
                  <strong>{c.name}</strong>
                  <span>{c.text}</span>
                </div>
              ))}
            </div>
            {liveSession?.isLive && (
              <form className="poc-live-chat-form" onSubmit={sendLiveComment}>
                <input
                  value={liveName}
                  onChange={e => setLiveName(e.target.value)}
                  placeholder="Your name"
                  maxLength={60}
                />
                <input
                  value={liveCommentText}
                  onChange={e => setLiveCommentText(e.target.value)}
                  placeholder="Ask Pooja or leave a comment"
                  maxLength={500}
                />
                <button type="submit">Send</button>
              </form>
            )}
          </div>
        )}
      </section>
      )}

      {!loading && latestEpisode && (
        <section className="poc-latest-section" aria-labelledby="poc-latest-title">
          <div className="poc-latest-label"><span>Latest episode</span><small>Start here</small></div>
          <article className="poc-latest-card">
            <div className="poc-latest-art" onClick={() => loadEp(latestEpisode)} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && loadEp(latestEpisode)}>
              {latestEpisode.coverArt
                ? <img src={latestEpisode.coverArt} alt={latestEpisode.title} />
                : <CoverPlaceholder epNum={latestEpisode.episodeNum} />}
              <button aria-label={`Play ${latestEpisode.title}`}><FaPlay /></button>
            </div>
            <div className="poc-latest-copy">
              <div className="poc-latest-meta">Episode {latestEpisode.episodeNum}{fmtDuration(latestEpisode.duration) && ` · ${fmtDuration(latestEpisode.duration)}`}</div>
              <h2 id="poc-latest-title">{latestEpisode.title}</h2>
              {latestEpisode.guest && <p className="poc-latest-guest">A conversation with <strong>{latestEpisode.guest}</strong>{latestEpisode.guestTitle && `, ${latestEpisode.guestTitle}`}</p>}
              {latestEpisode.description && <p className="poc-latest-desc">{latestEpisode.description}</p>}
              <div className="poc-latest-actions">
                <button onClick={() => loadEp(latestEpisode)} disabled={!latestEpisode.audioUrl}>
                  {playerEp?.id === latestEpisode.id && playerPlaying ? <><FaPause /> Pause episode</> : <><FaPlay /> Play episode</>}
                </button>
                <button onClick={() => shareEp(latestEpisode)} className="poc-latest-share"><FaShareAlt /> {copied === latestEpisode.id ? 'Copied' : 'Share'}</button>
              </div>
            </div>
          </article>
        </section>
      )}

      {/* ── EPISODES ── */}
      <section className="poc-episodes-section">
        <div className="poc-container">
          {!loading && archiveEpisodes.length > 0 && (
            <div className="poc-library-heading">
              <div><span>Browse the archive</span><h2>All episodes</h2></div>
              <p>Ideas and practical guidance for contracts, business and the human side of work.</p>
            </div>
          )}

          {loading && (
            <div className="poc-ep-grid">
              <EpSkeleton /><EpSkeleton /><EpSkeleton />
            </div>
          )}

          {!loading && episodes.length === 0 && (
            <div className="poc-empty">
              <div className="poc-empty-icon"><FaMicrophone /></div>
              <h3>Episodes coming soon</h3>
              <p>Subscribe via RSS and new episodes will appear in your podcast app.</p>
              <a href="/api/podcast/rss" target="_blank" rel="noreferrer" className="poc-rss-btn">
                <FaRss /> Get RSS link
              </a>
            </div>
          )}

          {archiveEpisodes.length > 0 && (
            <div className="poc-ep-grid">
              {archiveEpisodes.map((ep) => {
                const isActive = playerEp?.id === ep.id;
                return (
                  <article
                    key={ep.id}
                    id={`ep-${ep.episodeNum}`}
                    className={`poc-ep-card${isActive ? ' poc-ep-card--active' : ''}`}
                  >
                    {/* Cover art */}
                    <div
                      className="poc-ep-card-img"
                      onClick={() => loadEp(ep)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => e.key === 'Enter' && loadEp(ep)}
                      aria-label={`Play ${ep.title}`}
                    >
                      {ep.coverArt
                        ? <img src={ep.coverArt} alt={ep.title} loading="lazy" />
                        : <CoverPlaceholder epNum={ep.episodeNum} />
                      }
                      <div className={`poc-play-overlay${isActive && playerPlaying ? ' poc-play-overlay--pause' : ''}`}>
                        {isActive && playerPlaying ? <FaPause /> : <FaPlay />}
                      </div>
                    </div>

                    {/* Body */}
                    <div className="poc-ep-card-body">
                      <div className="poc-ep-card-meta-row">
                        <span className="poc-ep-num">Ep {ep.episodeNum}</span>
                        {ep.season > 1 && <span className="poc-ep-season">Season {ep.season}</span>}
                        <span className="poc-ep-dur">{fmtDuration(ep.duration)}</span>
                      </div>

                      <h3 className="poc-ep-title">{ep.title}</h3>

                      {ep.guest && (
                        <p className="poc-ep-guest">
                          with <strong>{ep.guest}</strong>
                          {ep.guestTitle && <span> · {ep.guestTitle}</span>}
                        </p>
                      )}

                      {ep.description && (
                        <p className="poc-ep-desc">
                          {ep.description.length > 110 ? ep.description.slice(0, 110) + '…' : ep.description}
                        </p>
                      )}

                      {ep.tags?.length > 0 && (
                        <div className="poc-ep-tags">
                          {ep.tags.slice(0, 4).map(t => <span key={t}>#{t}</span>)}
                        </div>
                      )}

                      <div className="poc-ep-actions">
                        <button
                          className={`poc-ep-play-btn${isActive ? ' poc-ep-play-btn--active' : ''}`}
                          onClick={() => loadEp(ep)}
                          disabled={!ep.audioUrl}
                        >
                          {isActive && playerPlaying
                            ? <><FaPause /> Pause</>
                            : <><FaPlay /> {isActive ? 'Resume' : 'Play episode'}</>
                          }
                        </button>
                        <button
                          className={`poc-ep-share-btn${copied === ep.id ? ' poc-ep-share-btn--copied' : ''}`}
                          onClick={() => shareEp(ep)}
                          title="Copy episode link"
                        >
                          {copied === ep.id ? '✓ Copied' : <><FaShareAlt /> Share</>}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <BlogFooter />

      {/* ── STICKY BOTTOM PLAYER ── */}
      {playerEp && (
        <div className="poc-player">
          {/* Left: artwork + title */}
          <div className="poc-player-left">
            {playerEp.coverArt
              ? <img src={playerEp.coverArt} alt={playerEp.title} className="poc-player-thumb" />
              : <div className="poc-player-thumb poc-player-thumb--placeholder"><FaMicrophone /></div>
            }
            <div className="poc-player-info">
              <span className="poc-player-ep-num">Ep {playerEp.episodeNum}</span>
              <strong className="poc-player-title">{playerEp.title}</strong>
              {playerEp.guest && <span className="poc-player-guest">with {playerEp.guest}</span>}
            </div>
          </div>

          {/* Centre: controls + timeline */}
          <div className="poc-player-centre">
            <div className="poc-player-btns">
              <button className="poc-player-skip" onClick={() => skip(-15)} title="Back 15s">
                <FaUndo /><span>15</span>
              </button>
              <button className="poc-player-play" onClick={togglePlay}>
                {playerPlaying ? <FaPause /> : <FaPlay />}
              </button>
              <button className="poc-player-skip" onClick={() => skip(15)} title="Forward 15s">
                <FaRedo /><span>15</span>
              </button>
            </div>
            <div className="poc-player-timeline">
              <span className="poc-player-time">{fmtTime(playerTime)}</span>
              <div
                ref={progressRef}
                className="poc-progress-track"
                onClick={seek}
                role="slider"
                aria-valuemin={0}
                aria-valuemax={playerDur}
                aria-valuenow={playerTime}
              >
                <div className="poc-progress-fill" style={{ width: `${pct}%` }} />
                <div className="poc-progress-thumb" style={{ left: `${pct}%` }} />
              </div>
              <span className="poc-player-time">{fmtTime(playerDur)}</span>
            </div>
          </div>

          {/* Right: speed + close */}
          <div className="poc-player-right">
            <div className="poc-speed-group">
              {SPEEDS.map(s => (
                <button
                  key={s}
                  className={`poc-speed-btn${playerSpeed === s ? ' poc-speed-btn--active' : ''}`}
                  onClick={() => changeSpeed(s)}
                >
                  {s === 1 ? '1×' : `${s}×`}
                </button>
              ))}
            </div>
            <button className="poc-player-close" onClick={closePlayer} title="Close player">✕</button>
          </div>
        </div>
      )}
    </div>
  );
}
